import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/authContext";
import styles from "./ForgotPasswordPage.module.css";
import SoftindexLogo from "../../assets/images/softindex-website-logo.webp";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const { forgotPassword } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email.trim()) {
      setError("Please enter your email address");
      return;
    }

    setLoading(true);
    setError("");

    try {
      await forgotPassword(email);
      setSuccess(true);
    } catch (err) {
      setError(err.message || "Failed to send reset email. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate("/auth");
  };

  if (success) {
    return (
      <div className={styles.authPage}>
        <div className={styles.container}>
          <div className={styles.logoSection}>
            <img
              src={SoftindexLogo}
              alt="Softindex Logo"
              className={styles.logo}
            />
          </div>

          <div className={styles.content}>
            <div className={styles.successIcon}>
              <svg
                width="64"
                height="64"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22,4 12,14.01 9,11.01" />
              </svg>
            </div>

            <h1 className={styles.title}>Check Your Email</h1>
            <p className={styles.subtitle}>
              We&apos;ve sent a password reset link to <strong>{email}</strong>
            </p>

            <div className={styles.infoBox}>
              <p>
                If you don&apos;t see the email in your inbox, please check your
                spam folder. The reset link will expire in 15 minutes.
              </p>
            </div>

            <div className={styles.buttonGroup}>
              <button
                type="button"
                onClick={handleBackToLogin}
                className={styles.backButton}
              >
                Back to Login
              </button>
              <button
                type="button"
                onClick={() => setSuccess(false)}
                className={styles.resendButton}
              >
                Send Another Email
              </button>
            </div>

            <div className={styles.helpText}>
              <p>
                Having trouble?{" "}
                <a
                  href="https://softindex.ro/contact"
                  className={styles.helpLink}
                >
                  Contact our support team
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.authPage}>
      <div className={styles.container}>
        <div className={styles.logoSection}>
          <img
            src={SoftindexLogo}
            alt="Softindex Logo"
            className={styles.logo}
          />
        </div>

        <div className={styles.content}>
          <h1 className={styles.title}>Forgot Password?</h1>
          <p className={styles.subtitle}>
            No worries! Enter your email address and we&apos;ll send you a link
            to reset your password.
          </p>

          {error && (
            <div className={styles.errorMessage}>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className={styles.errorIcon}
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
              </svg>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.inputGroup}>
              <input
                type="email"
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={styles.input}
                disabled={loading}
                autoFocus
              />
            </div>

            <button
              type="submit"
              disabled={!email.trim() || loading}
              className={styles.submitButton}
            >
              {loading ? (
                <div className={styles.spinner} />
              ) : (
                "Send Reset Link"
              )}
            </button>
          </form>

          <div className={styles.divider}>
            <span>or</span>
          </div>

          <button
            type="button"
            onClick={handleBackToLogin}
            className={styles.backToLoginButton}
            disabled={loading}
          >
            Back to Login
          </button>

          <div className={styles.helpText}>
            <p>
              Remember your password?{" "}
              <button
                type="button"
                onClick={handleBackToLogin}
                className={styles.loginLink}
                disabled={loading}
              >
                Sign in here
              </button>
            </p>
            <p>
              Having issues?{" "}
              <a
                href="https://softindex.ro/contact"
                className={styles.helpLink}
              >
                Contact our support team
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
